/**
 * Status bar theming utilities for PWA
 * Ensures consistent status bar colors across different platforms and scenarios
 */

export const STATUS_BAR_COLOR = '#1e3a8a'; // Dark blue matching our app theme
export const STATUS_BAR_COLOR_LIGHT = '#3b82f6'; // Lighter blue for light mode scenarios

/**
 * Dynamically update status bar theming based on the current app state
 */
export const updateStatusBarTheme = (isDarkMode: boolean = true) => {
  const color = isDarkMode ? STATUS_BAR_COLOR : STATUS_BAR_COLOR_LIGHT;
  
  // Update theme-color meta tag
  let themeColorMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', color);
  }
  
  // Update for Windows Phone
  let msNavButtonMeta = document.querySelector('meta[name="msapplication-navbutton-color"]') as HTMLMetaElement;
  if (msNavButtonMeta) {
    msNavButtonMeta.setAttribute('content', color);
  }
  
  // iOS specific - update status bar style
  let iosStatusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]') as HTMLMetaElement;
  if (iosStatusBarMeta) {
    // For dark themes, use black-translucent to allow our background to show through
    // For light themes, use default
    iosStatusBarMeta.setAttribute('content', isDarkMode ? 'black-translucent' : 'default');
  }
};

/**
 * Initialize status bar theming when the app loads
 */
export const initializeStatusBarTheming = () => {
  // Set initial theme
  updateStatusBarTheme(true); // Default to dark mode
  
  // Listen for PWA installation state changes
  window.addEventListener('beforeinstallprompt', () => {
    console.log('[StatusBar] PWA install prompt detected');
    updateStatusBarTheme(true);
  });
  
  // Listen for PWA app state changes
  window.addEventListener('appinstalled', () => {
    console.log('[StatusBar] PWA installed - updating status bar theme');
    updateStatusBarTheme(true);
  });
  
  // Handle display mode changes (standalone vs browser)
  const mediaQuery = window.matchMedia('(display-mode: standalone)');
  mediaQuery.addEventListener('change', (e) => {
    console.log('[StatusBar] Display mode changed to:', e.matches ? 'standalone' : 'browser');
    updateStatusBarTheme(true);
  });
  
  // iOS specific: Handle viewport changes when app enters/exits fullscreen
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
    let lastViewportHeight = window.innerHeight;
    
    window.addEventListener('resize', () => {
      const currentHeight = window.innerHeight;
      const heightDiff = Math.abs(currentHeight - lastViewportHeight);
      
      // If significant height change, likely status bar visibility changed
      if (heightDiff > 50) {
        setTimeout(() => updateStatusBarTheme(true), 100);
        lastViewportHeight = currentHeight;
      }
    });
  }
};

/**
 * Apply safe area insets for devices with notches/rounded corners
 */
export const applySafeAreaInsets = () => {
  const root = document.documentElement;
  
  // Only apply if safe area insets are supported
  if (CSS.supports('padding-top: env(safe-area-inset-top)')) {
    root.style.setProperty('--safe-area-top', 'env(safe-area-inset-top)');
    root.style.setProperty('--safe-area-bottom', 'env(safe-area-inset-bottom)');
    root.style.setProperty('--safe-area-left', 'env(safe-area-inset-left)');
    root.style.setProperty('--safe-area-right', 'env(safe-area-inset-right)');
  }
};

/**
 * Check if the app is running in PWA mode
 */
export const isPWAMode = (): boolean => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    // @ts-ignore - Navigator.standalone is iOS specific
    window.navigator.standalone === true
  );
};

/**
 * Get the current status bar height (iOS specific)
 */
export const getStatusBarHeight = (): number => {
  if (isPWAMode() && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
    // In PWA mode on iOS, calculate based on safe area
    const safeAreaTop = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top') || '0');
    return safeAreaTop || 44; // Default to 44px if safe area not available
  }
  return 0;
};